import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import { toast } from 'sonner';
import { AppShell } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { SectionTitle } from '@/components/ui/SectionTitle';
import {
  PLAN_ORDER,
  PLANS,
  priceCopyFor,
  TIER_RANK,
  type BillingInterval,
  type PlanCopy,
} from '@/constants/plans';
import { cn } from '@/lib/cn';
import { billingService } from '@/services/billing.service';
import { ApiError } from '@/services/http';
import { useAccountStore } from '@/store/account.store';
import type { PlanTier } from '@/types/api';

/**
 * Pricing — `/pricing`. Two-column comparison (Free / Family). Ollie Plus is
 * intentionally hidden here — Family is the single paid tier offered, carrying
 * the consolidated perk list (see `PLANS.family`). Plus still exists in the
 * catalogue for existing subscribers' Account page. Each card surfaces:
 *
 *   - Plan name + monthly price
 *   - Feature checklist
 *   - CTA button
 *
 * Behavior depends on the visitor's current state:
 *   - Free tier viewing a paid plan → "Upgrade to <name>" → Stripe Checkout.
 *   - Already-paid viewing their current plan → button disabled, "Current plan".
 *   - Already-paid viewing a different paid plan → "Upgrade to" or
 *     "Downgrade to" depending on direction → Customer Portal session
 *     deep-linked to the plan-update flow (`flow_data.subscription_update`)
 *     so Stripe drops the user straight on the plan-switch screen instead
 *     of the portal home page where the option can be hard to find.
 *   - Paid subscriber viewing Free → "Downgrade to Free" → portal session
 *     deep-linked to the cancellation flow.
 *
 * On `?checkout=canceled` (Stripe sends users back here when they bail
 * out of Checkout), we show a soft toast — no error styling, since
 * cancellation isn't actually an error.
 */
export function PricingPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const account = useAccountStore((s) => s.account);
  const planTier = useAccountStore((s) => s.planTier());
  // BILLING signal, NOT the access selector. This drives the Checkout-vs-portal
  // CTA routing: a free-trial user has premium access but NO Stripe
  // subscription, so they must go through Checkout (which mints their first
  // subscription) — never the portal's `subscription_update` flow, which has
  // nothing to update. Reading `subscription.isPaid` directly keeps trial users
  // on the free-user CTA path.
  const isPaid = useAccountStore((s) => s.account?.subscription?.isPaid === true);
  const [busyTier, setBusyTier] = useState<PlanTier | null>(null);
  // Billing cadence for new checkouts. Defaults to Yearly — the better-value
  // option we want to steer people toward. Only the paid card's price reacts
  // to this; Free is always $0. Existing subscribers change their cadence via
  // the Customer Portal (the toggle doesn't drive paid-to-paid swaps).
  const [interval, setBillingInterval] = useState<BillingInterval>('year');

  // Show a one-shot toast and strip the query param so a refresh doesn't
  // re-fire the message. We don't navigate elsewhere — the user might
  // legitimately want to try again from this page.
  useEffect(() => {
    if (searchParams.get('checkout') === 'canceled') {
      toast.message('Checkout canceled', {
        description: 'No worries — your plan is unchanged.',
      });
      const next = new URLSearchParams(searchParams);
      next.delete('checkout');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handlePaidUpgrade = async (tier: 'plus' | 'family') => {
    if (!account) {
      // ProtectedRoute should have ensured this, but be defensive.
      navigate('/login');
      return;
    }
    setBusyTier(tier);
    try {
      const { url } = await billingService.createCheckoutSession({ tier, interval });
      window.location.assign(url);
    } catch (err) {
      setBusyTier(null);
      toast.error(err instanceof ApiError ? err.message : 'Could not start checkout.');
    }
  };

  const handleOpenPortal = async (tier: PlanTier, intent: 'manage' | 'update' | 'cancel') => {
    setBusyTier(tier);
    try {
      const { url } = await billingService.createPortalSession({ intent });
      window.location.assign(url);
    } catch (err) {
      setBusyTier(null);
      toast.error(err instanceof ApiError ? err.message : 'Could not open the billing portal.');
    }
  };

  return (
    <AppShell mode="parent">
      <div className="flex flex-col gap-8">
        <SectionTitle kicker="Pricing" heading="Pick the plan that fits your household" />

        <IntervalToggle value={interval} onChange={setBillingInterval} />

        <div className="mx-auto grid w-full max-w-3xl grid-cols-1 gap-6 md:grid-cols-2">
          {/* Ollie Plus is hidden on the pricing page — only Free + Family. */}
          {PLAN_ORDER.filter((tier) => tier !== 'plus').map((tier) => {
            const plan = PLANS[tier];
            const isCurrent = tier === planTier;
            // Compare ranks so we can pick "Upgrade" vs "Downgrade" copy
            // for paid-to-paid transitions. The direction also drives the
            // portal `flow_data` intent: any paid-to-paid swap goes through
            // `subscription_update`; a paid user moving to free triggers
            // `subscription_cancel`.
            const isDowngrade = TIER_RANK[tier] < TIER_RANK[planTier];
            return (
              <PlanCard
                key={tier}
                plan={plan}
                interval={interval}
                isCurrent={isCurrent}
                isPaidSubscriber={isPaid}
                isDowngrade={isDowngrade}
                busy={busyTier === tier}
                anyBusy={busyTier !== null}
                onUpgrade={tier === 'free' ? undefined : () => handlePaidUpgrade(tier)}
                onOpenPortal={(intent) => handleOpenPortal(tier, intent)}
              />
            );
          })}
        </div>

        <p className="text-center text-[12px] text-or-slate-500">
          Paid plans renew {interval === 'year' ? 'yearly' : 'monthly'}. Cancel anytime from your
          account.
        </p>
      </div>
    </AppShell>
  );
}

interface IntervalToggleProps {
  value: BillingInterval;
  onChange: (next: BillingInterval) => void;
}

/**
 * Monthly / Yearly segmented control. Reuses the app's pill-segmented
 * visual idiom (rounded-full border + filled active segment, as in
 * `AdminSectionTabs`). Two real radio buttons so screen readers announce
 * the group correctly. The Yearly segment carries a small savings hint to
 * tease the discount before the card price updates.
 */
function IntervalToggle({ value, onChange }: IntervalToggleProps) {
  return (
    <div
      role="radiogroup"
      aria-label="Billing interval"
      className="mx-auto inline-flex items-center gap-1 self-center rounded-full border border-or-slate-200 bg-white p-1"
    >
      <IntervalOption
        label="Monthly"
        selected={value === 'month'}
        onSelect={() => onChange('month')}
      />
      <IntervalOption
        label="Yearly"
        hint="Save 46%"
        selected={value === 'year'}
        onSelect={() => onChange('year')}
      />
    </div>
  );
}

interface IntervalOptionProps {
  label: string;
  hint?: string;
  selected: boolean;
  onSelect: () => void;
}

function IntervalOption({ label, hint, selected, onSelect }: IntervalOptionProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[13px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-orange-500/40',
        selected
          ? 'bg-or-orange-500 text-white shadow-sm'
          : 'text-or-slate-600 hover:text-or-slate-900',
      )}
    >
      {label}
      {hint ? (
        <span
          className={cn(
            'rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide',
            selected ? 'bg-white/20 text-white' : 'bg-or-green-100 text-or-green-700',
          )}
        >
          {hint}
        </span>
      ) : null}
    </button>
  );
}

interface PlanCardProps {
  plan: PlanCopy;
  /** Selected billing cadence — picks which of the plan's prices to show. */
  interval: BillingInterval;
  isCurrent: boolean;
  isPaidSubscriber: boolean;
  /** True when this card represents a tier below the user's current plan. */
  isDowngrade: boolean;
  busy: boolean;
  anyBusy: boolean;
  /** Provided only for paid tiers. */
  onUpgrade?: () => void;
  /**
   * Open a Stripe Customer Portal session, deep-linked to a specific
   * flow. `'update'` for paid-to-paid swaps, `'cancel'` for paid-to-free.
   */
  onOpenPortal: (intent: 'manage' | 'update' | 'cancel') => void;
}

function PlanCard({
  plan,
  interval,
  isCurrent,
  isPaidSubscriber,
  isDowngrade,
  busy,
  anyBusy,
  onUpgrade,
  onOpenPortal,
}: PlanCardProps) {
  // Free has no annual price, so `priceCopyFor` falls back to monthly ($0).
  const price = priceCopyFor(plan, interval);
  // CTA decision tree (kept in the component so the rationale lives next
  // to the rendered button — it's the single trickiest thing on this page):
  //   - Showing the user's current plan → "Current plan", disabled.
  //   - Free tier, user is on a paid plan → "Downgrade to Free" → portal
  //     session deep-linked to the cancellation flow. We don't reimplement
  //     cancel ourselves; Stripe owns that flow.
  //   - Free tier, user is also free → "Current plan", disabled. (We
  //     handled `isCurrent` above so this branch is unreachable, but
  //     keeping the explicit handling helps readability.)
  //   - Paid tier the user isn't on → either "Upgrade to <name>" or
  //     "Downgrade to <name>" depending on the direction of the move.
  //     Free → paid goes through Checkout; paid → different paid goes
  //     through the portal's `subscription_update` deep-link so Stripe
  //     handles proration instead of us minting a second subscription.
  let ctaLabel: string;
  let ctaDisabled = false;
  let onClick: (() => void) | undefined;

  if (isCurrent) {
    ctaLabel = 'Current plan';
    ctaDisabled = true;
  } else if (plan.tier === 'free') {
    if (isPaidSubscriber) {
      ctaLabel = 'Downgrade to Free';
      onClick = () => onOpenPortal('cancel');
    } else {
      ctaLabel = 'Current plan';
      ctaDisabled = true;
    }
  } else if (isPaidSubscriber) {
    ctaLabel = (isDowngrade ? 'Downgrade to ' : 'Upgrade to ') + plan.name;
    onClick = () => onOpenPortal('update');
  } else {
    ctaLabel = plan.ctaLabel;
    onClick = onUpgrade;
  }

  if (busy) ctaLabel = 'Loading…';
  if (anyBusy && !busy) ctaDisabled = true;

  return (
    <article
      className={cn(
        'relative flex flex-col gap-4 rounded-3xl border bg-white p-6 shadow-or-card',
        plan.badge ? 'border-or-orange-300' : 'border-or-slate-200',
      )}
    >
      {plan.badge ? (
        <div className="absolute -top-3 left-6">
          <Chip variant="orange">{plan.badge}</Chip>
        </div>
      ) : null}
      <header className="flex flex-col gap-1">
        <div className="font-display text-[20px] font-bold text-or-slate-900">{plan.name}</div>
        {/* Stacked vertically: the crossed-out "compare at" price sits above
            the actual price. Reads better on mobile / narrow plan columns than
            a side-by-side row. */}
        <div className="flex flex-col items-start gap-0.5 font-display text-[28px] font-bold leading-tight text-or-orange-700">
          {price.compareAtLabel ? (
            <span className="text-[18px] font-semibold text-or-slate-400 line-through">
              {price.compareAtLabel}
            </span>
          ) : null}
          <span className="flex items-center gap-2">
            {price.priceLabel}
            {price.savingsBadge ? (
              <Chip variant="live" className="text-[11px] font-semibold normal-case">
                {price.savingsBadge}
              </Chip>
            ) : null}
          </span>
        </div>
        {price.subLabel ? (
          <p className="text-[12px] font-medium text-or-slate-500">{price.subLabel}</p>
        ) : null}
        {price.discountNote ? (
          <p className="text-[12px] font-semibold uppercase tracking-wide text-or-orange-700">
            {price.discountNote}
          </p>
        ) : null}
        <p className="text-[13px] text-or-slate-600">{plan.tagline}</p>
      </header>

      <ul className="flex flex-col gap-2 border-t border-or-slate-100 pt-4 text-[13px] text-or-slate-700">
        {plan.features.map((feature) => (
          <li key={feature} className="flex items-start gap-2">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-or-orange-600" aria-hidden />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto pt-2">
        <Button
          variant={isCurrent ? 'secondary' : 'primary'}
          size="lg"
          className="w-full"
          disabled={ctaDisabled || busy}
          onClick={onClick}
        >
          {ctaLabel}
        </Button>
      </div>
    </article>
  );
}
